import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaLinkedin } from 'react-icons/fa';
import { GlowingEffect } from './border-effect/GlowingEffect';

const Header = ({ bgColor = '#242424' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [location.pathname]);


  const [menuHeight, setMenuHeight] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const updateMenuHeight = () => {
      setMenuHeight(window.visualViewport?.height ?? window.innerHeight);
    };

    updateMenuHeight();
    window.addEventListener('resize', updateMenuHeight);
    window.visualViewport?.addEventListener('resize', updateMenuHeight);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('resize', updateMenuHeight);
      window.visualViewport?.removeEventListener('resize', updateMenuHeight);
      setMenuHeight(null);
    };
  }, [isOpen]);


  const mobileMenu = isOpen ? (
    <div
      className="fixed inset-0 z-[10000] grid grid-rows-[auto_1fr_auto] w-full bg-[#00E599] text-black overflow-hidden overscroll-none md:hidden"
      style={{ height: menuHeight ? `${menuHeight}px` : '100dvh' }}
    >
      <div className="flex justify-between items-center px-6 py-4 border-b border-black">
        <Link to="/" className="text-2xl font-bold" onClick={toggleMenu}>
          <img
            src="/company_img/logo_sm.svg"
            alt="Techxplore provides a unified digital strategy that includes enterprise app architecture, mobile-first development, responsive web platforms, and seamless UI/UX design to deliver functional and beautiful user experiences."
            className="h-10"
          />
        </Link>
        <button onClick={toggleMenu} className="relative w-6 h-6 focus:outline-none" aria-label="Close menu">
          <span className="absolute top-1/2 left-0 w-full h-[2px] bg-black transform rotate-45"></span>
          <span className="absolute top-1/2 left-0 w-full h-[2px] bg-black transform -rotate-45"></span>
        </button>
      </div>

      <nav className="flex flex-col px-6 py-8 min-h-0 overflow-y-auto">
        {['Services', 'Case studies', 'About us', 'Blog', 'Contact'].map((label, i) =>
          label === 'Blog' ? (
            <button
              key={i}
              type="button"
              onClick={() => {
                window.open('https://blog.techxplore.io/', '_blank');
                toggleMenu();
              }}
              className="font-roboto font-normal text-[25px] leading-[48px] tracking-[0%] hover:text-white transition text-left bg-transparent border-none p-0 cursor-pointer"
            >
              {label}
            </button>
          ) : (
            <Link
              key={i}
              to={`/${label.toLowerCase().replace(' ', '-')}`}
              onClick={toggleMenu}
              className="font-roboto font-normal text-[25px] leading-[48px] tracking-[0%] hover:text-white transition"
            >
              {label}
            </Link>
          )
        )}
      </nav>

      <div className="px-6 pb-8 pt-4 flex space-x-4 items-center border-t border-black/10">
        <a href="https://x.com/techxploreio" target="_blank" rel="noopener noreferrer" aria-label="X.com">
          <img src="/icons/x.com_icon.svg" alt="X.com" className="h-8 w-8" />
        </a>
        <a href="https://www.linkedin.com/company/techxploreio" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
          <img src="/icons/linkedin_icon.svg" alt="LinkedIn" className="h-8 w-8" />
        </a>
      </div>
    </div>
  ) : null;

  return createPortal(
    <>
 <header
      className={`group text-white z-[9999] fixed top-4 backdrop-blur-lg rounded-[20px] transition-all duration-500 transform-gpu
        ${isScrolled
          ? 'left-3 w-[93%] md:left-12 md:w-[93%] shadow-lg backdrop-blur-xl'
          : 'left-1.5 w-[96%] md:left-8 md:w-[96%] shadow-md'
        }`}
      style={{
        backgroundColor: isScrolled ? `${bgColor}E6` : `${bgColor}B3`,
      }}
>      <GlowingEffect
        blur={0}
        borderWidth={2}
        spread={20}
        glow={true}
        disabled={false}
        proximity={64}
        inactiveZone={0.01}
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-[20px]"
      />
      <div className="relative z-10 flex items-center justify-between py-4 px-6 lg:px-7">
        <Link to="/" className="text-2xl font-bold">
          <img src="/company_img/logo.svg"  alt='Techxplore – Complete IT Solutions Including Android, iOS, and Enterprise Applications' className='h-8 md:h-9'></img>
        </Link>

       <button onClick={toggleMenu} className="md:hidden relative flex flex-col justify-between w-6 h-6 focus:outline-none">
         <span className="block h-[3px] w-3 bg-white rounded-sm self-end transition-all duration-300"></span>
        <span className="block h-[3px] w-[24px] bg-white rounded-sm transition-all duration-300"></span>
        <span className="block h-[3px] w-3 bg-white rounded-sm self-start transition-all duration-300"></span>
      </button>

        {/* Desktop Menu */}
      <nav className=" space-x-1 hidden md:flex ">
        {['services', 'case-studies', 'about-us', 'blog', 'contact'].map((route) => {
          const isContact = route === 'contact';
          const linkClassName = ` px-6 py-1 text-body font-normal font-roboto transition duration-300 text-[#FFFFFF] ${
            isContact ? 'bg-[#FFFFFF] text-[#000000] rounded-[50px] border border-transparent hover:text-white hover:bg-transparent hover:border-white'  : ''
          }`;

          if (route === 'blog') {
            return (
              <button
                key={route}
                type="button"
                onClick={() => window.open('https://blog.techxplore.io/', '_blank')}
                className={`${linkClassName} bg-transparent border-none cursor-pointer`}
              >
                Blog
              </button>
            );
          }

          return (
            <Link
              key={route}
              to={`/${route}`}
              className={linkClassName}
            >
              {route.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
            </Link>
          );
        })}
      </nav>
      </div>
    </header>
    {mobileMenu}
    </>,
    document.body
  );
};
export default Header;
